/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import { defineStore } from "pinia";

/**
 * useSelectionStore is a state management store that handles the selection of nodes, files, and folders.
 * It provides actions to manipulate the selection state, such as adding or removing nodes, managing single or
 * multiple selections, and updating related states like selection visibility and page selection status.
 *
 * State Properties:
 * - nodes: Array holding the currently selected nodes.
 * - files: Array holding the currently selected file nodes.
 * - folders: Array holding the currently selected folder nodes.
 * - isCurrentPageFullySelected: Boolean indicating if the current page is fully selected.
 * - isSelectionRemovalVisible: Boolean indicating if the selection removal option should be visible.
 *
 * Actions:
 * - resetSelection: Clears the current selection and resets nodes.
 * - addNode: Adds a node to the current selection.
 * - removeNode: Removes a node from the current selection.
 * - selectSingleNode: Replaces the current selection with a single node.
 * - selectNodes: Replaces the current selection with a provided list of nodes.
 * - setIsCurrentPageFullySelected: Updates the state indicating if the current page is fully selected.
 * - setIsSelectionRemovalVisible: Updates the state indicating if the selection removal option is visible.
 */
export const useSelectionStore = defineStore({
  id: "SelectionStore",
  state: () => ({
    nodes: [],
    files: [],
    folders: [],
    isCurrentPageFullySelected: false,
    isSelectionRemovalVisible: false,
  }),
  actions: {
    resetSelection() {
      this.selectNodes([]);
    },
    /**
     * Add node to the current selection
     * @param node Node to add
     */
    addNode(node) {
      if (node) {
        this.selectNodes([...this.nodes, ...node]);
      }
    },
    /**
     * Remove node from the current selection
     * @param node Node to remove
     */
    removeNode(node) {
      if (node) {
        this.selectNodes(this.nodes.filter((n) => n.id !== node.id));
      }
    },
    /**
     * Replace current selection with a single node.
     * @param node The seelcted node.
     */
    selectSingleNode(node) {
      if (node) {
        this.selectNodes([node]);
      }
    },
    /**
     * Replace current selection with nodes.
     * @param selNodes List of nodes or undefined to reset current list.
     */
    selectNodes(selNodes) {
      if (selNodes) {
        this.$patch({
          nodes: selNodes,
          files: selNodes.filter((node) => node.isFile),
          folders: selNodes.filter((node) => node.isFolder),
        });
      }
    },
    /**
     *
     * @param isCurrentPageFullySelected
     */
    setIsCurrentPageFullySelected(isCurrentPageFullySelected) {
      this.isCurrentPageFullySelected = isCurrentPageFullySelected;
    },
    /**
     *
     * @param isSelectionRemovalVisible
     */
    setIsSelectionRemovalVisible(isSelectionRemovalVisible) {
      this.isSelectionRemovalVisible = isSelectionRemovalVisible;
    },
  },
});
